import { GuidedTour } from "@components/common/GuidedTour";
import { usePreferences } from "@hooks";
import { useTour } from "@hooks/use-tour";
import { useEffect } from "react";

const steps = [
  {
    // Step 0: Inbox on dashboard
    skipBeacon: true,
    target: '[data-testid="dashboard-inbox"]',
    placement: "left",
    title: "Your Inbox",
    content:
      "After you record a note, it appears here in your inbox. Notes with a green 'Review' badge are ready for you to check and approve. Click one to open it!",
    blockTargetInteraction: false,
    buttons: [],
  },
  {
    // Step 1: On the note page — review it
    skipBeacon: true,
    target: '[data-testid="submit-note-button"]',
    placement: "center",
    title: "Review Your Note",
    content:
      "It's important to carefully review generated notes for accuracy and completeness. Make any edits you need — you can change text, add details, or fix anything that doesn't look right.",
  },
  {
    // Step 2: Click Mark as Done
    skipBeacon: true,
    target: '[data-testid="submit-note-button"]',
    placement: "bottom",
    title: "Mark as Done",
    content:
      "When you're happy with the note, click \"Mark as Done\" to mark it as complete and move it to the 'Ready to Export' section of your inbox.",
    blockTargetInteraction: false,
    buttons: [],
  },
  {
    // Step 3: Back on dashboard — click Expand
    skipBeacon: true,
    target: '[data-testid="expand-inbox-button"]',
    placement: "bottom",
    title: "View Full Inbox",
    content:
      'Click "Expand" to open the full inbox where you can track the journey of all your notes.',
    blockTargetInteraction: false,
    buttons: [],
  },
  {
    // Step 4: On /inbox — point to Ready to Export tab
    skipBeacon: true,
    target: '[data-testid="inboxTabs-tab-forExport"]',
    placement: "bottom",
    title: "Ready to Export",
    content:
      "Notes you've marked as done appear here, ready to be exported to your PIMS. Great job!",
    blockTargetInteraction: false,
  },
];

export default function ReviewNoteTour() {
  const { reviewNoteTourSeen, setReviewNoteTourSeen } =
    usePreferences();

  const {
    run,
    stepIndex,
    setStepIndex,
    setRun,
    handleCallback,
    completeTour,
  } = useTour({
    name: "Review and Mark Note as Done",
    steps,
    isActive: !reviewNoteTourSeen,
    onComplete: () => setReviewNoteTourSeen(true),
    pollForStart: true,
    canStart: () =>
      !!document.querySelector(
        '[data-testid="note-ready-for-review"]',
      ),
  });

  // Poll to auto-advance steps based on DOM state
  useEffect(() => {
    if (!run) return;

    const interval = setInterval(() => {
      const nextStep = steps[stepIndex + 1];

      const advanceTo = (nextIndex) => {
        clearInterval(interval);
        setRun(false);
        setStepIndex(nextIndex);
        // Wait for React to flush the step change and DOM to settle
        setTimeout(() => setRun(true), 500);
      };

      // Step 0 (inbox): advance when note page loads (submit button appears)
      if (
        stepIndex === 0 &&
        nextStep &&
        document.querySelector(nextStep.target)
      ) {
        advanceTo(1);
        return;
      }

      // Step 2 (mark as done): advance when button disappears (note saved, back to dashboard)
      if (
        stepIndex === 2 &&
        !document.querySelector(steps[2].target) &&
        document.querySelector(steps[3].target)
      ) {
        advanceTo(3);
        return;
      }

      // Step 3 (expand): advance when inbox tabs appear (navigated to /inbox)
      if (
        stepIndex === 3 &&
        document.querySelector(steps[4].target)
      ) {
        advanceTo(4);
        return;
      }
    }, 500);

    return () => clearInterval(interval);
  }, [run, stepIndex, setRun, setStepIndex, completeTour]);

  return (
    <GuidedTour
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      onEvent={handleCallback}
      locale={{ last: "Got it!" }}
    />
  );
}
